'use client';
import { useMemo } from 'react';
import { resolveChatKeybinds, type EffectiveChatKeybinds, type ParsedBinding, type WebKeybindAction } from '../../../packages/plugin-shared/chatKeys.mjs';
import { useMyTerminalSettings } from '../../lib/queries';

export interface ChatKeybindings {
  effective: EffectiveChatKeybinds<'web'> | null;
  rows: { action: WebKeybindAction; bindings: ParsedBinding[]; custom: boolean }[];
  loading: boolean;
  error: Error | null;
}

/** One confirmed account settings projection for both dispatch and the shortcut editor. */
export function useChatKeybindings(): ChatKeybindings {
  const query = useMyTerminalSettings();
  return useMemo(() => {
    if (query.error) return { effective: null, rows: [], loading: false, error: query.error };
    if (!query.data) return { effective: null, rows: [], loading: true, error: null };
    if (!query.data.chatKeybinds || typeof query.data.chatKeybinds !== 'object' || Array.isArray(query.data.chatKeybinds)) {
      return { effective: null, rows: [], loading: false, error: new Error('Invalid stored web keymap') };
    }
    const effective = resolveChatKeybinds(query.data.chatKeybinds, { surface: 'web' });
    if (effective.issues.length) return { effective: null, rows: [], loading: false, error: new Error(`Invalid web keymap: ${effective.issues.map(issue => issue.message).join('; ')}`) };
    return { effective, rows: effective.actions.map(action => ({ action, bindings: effective.bindings.get(action) ?? [], custom: effective.custom.has(action) })), loading: false, error: null };
  }, [query.data, query.error]);
}
